import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { buildAmazonSearchUrl } from '../utils/amazon';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import EmailAuthSheet from '../components/EmailAuthSheet';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import ShareModal from '../components/ShareModal';
import RecipeView from '../components/RecipeView';
import WebsiteAnalysisView from '../components/WebsiteAnalysisView';
import HotelAnalysisView from '../components/HotelAnalysisView';
import PlaceView from '../components/PlaceView';
import { SongDeepDive } from '../components/SongDeepDive';
import { ProcessingSteps, ProcessingBlockPlaceholder } from '../components/ProcessingIndicator';
import { ReminderPicker } from '../components/ReminderPicker';
import { ReminderAlerts } from '../components/ReminderAlert';
import { useReminderPolling } from '../hooks/useReminderPolling';
import { Toaster, toast } from 'sonner';
import AskBookmarkInline from '../components/AskBookmarkInline';
import { Lock, Eye } from 'lucide-react';
import { 
    ArrowLeft, ExternalLink, Play, FileText, Image as ImageIcon, 
    Loader2, RefreshCw, Trash2, Edit2, Save, X, Tag, Clock,
    Sparkles, Link as LinkIcon, Copy, Check, Bookmark, MoreVertical,
    Cpu, Briefcase, GraduationCap, Film, Heart, Coffee, Newspaper,
    Zap, UtensilsCrossed, Plane, Folder, AlertTriangle, StickyNote, Share2, ChevronRight,
    Compass, ExternalLink as ExternalLinkIcon, Camera, ImagePlus, User, MapPin, Calendar, BookOpen,
    MessageSquarePlus, BrainCircuit, ScrollText, MessageSquareText, MessageCircleQuestion,
    TrendingUp, Clapperboard, HeartPulse, Target, ChefHat, Scale, Wine,
    Lightbulb, Flame, Flag, Trophy, Mic, Tv, Star, Rocket,
    Music, Home, Globe, ShoppingBag, Palette, Code, Dumbbell, MessageCircle, Car, Baby, Dog, Gamepad2, Landmark, Stethoscope, Search, X as XIcon
} from 'lucide-react';
import InlineMediaPlayer from '../components/InlineMediaPlayer';
import { isEmbeddableMediaUrl } from '../lib/media-embed';

const API = '/api';

// Render notes with inline images (markdown-style ![alt](url))
const NoteContent = ({ text, dir }) => {
    if (!text) return null;
    const parts = text.split(/(!\[.*?\]\(.*?\))/g);
    return (
        <div className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap" dir={dir}>
            {parts.map((part, i) => {
                const imgMatch = part.match(/^!\[(.*?)\]\((.*?)\)$/);
                if (imgMatch) {
                    return (
                        <img
                            key={`img-${i}-${imgMatch[2].slice(-20)}`}
                            src={imgMatch[2]}
                            alt={imgMatch[1]}
                            className="my-3 rounded-xl max-w-full max-h-80 object-contain border border-border/40"
                            data-testid={`note-image-${i}`}
                        />
                    );
                }
                return <React.Fragment key={`text-${i}`}>{part}</React.Fragment>;
            })}
        </div>
    );
};

// Category icon mapping
const CATEGORY_ICONS = {
    'cpu': Cpu, 'brain-circuit': BrainCircuit,
    'briefcase': Briefcase, 'trending-up': TrendingUp,
    'graduation-cap': GraduationCap,
    'film': Film, 'clapperboard': Clapperboard,
    'heart': Heart, 'heart-pulse': HeartPulse,
    'coffee': Coffee, 'newspaper': Newspaper,
    'zap': Zap, 'target': Target,
    'utensils': UtensilsCrossed, 'chef-hat': ChefHat,
    'plane': Plane, 'map-pin': MapPin, 'compass': Compass,
    'folder': Folder, 'music': Music, 'camera': Camera,
    'book': BookOpen, 'home': Home, 'globe': Globe,
    'shopping': ShoppingBag, 'palette': Palette, 'code': Code,
    'dumbbell': Dumbbell, 'chat': MessageCircle,
    'car': Car, 'baby': Baby, 'dog': Dog,
    'gamepad': Gamepad2, 'landmark': Landmark, 'health': Stethoscope,
    'scale': Scale, 'wine': Wine, 'lightbulb': Lightbulb,
    'flame': Flame, 'flag': Flag, 'trophy': Trophy,
    'mic': Mic, 'tv': Tv, 'star': Star, 'rocket': Rocket,
};

const BookmarkDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading: authLoading, checkAuth } = useAuth();

    // Anonymous "try-it" mode: when the URL has `?anon=1&device_id=X`
    // (set by TryItFlow after the anonymous processing pipeline
    // completes), fetch the bookmark from the anon endpoint, skip the
    // auth-gate, and suppress destructive/edit affordances. A floating
    // "Keep this forever" CTA overlays the bottom of the page and
    // opens the sign-up flow, stashing the pending claim in
    // localStorage so `AuthContext.checkAuth` can migrate the bookmark
    // to the newly-authenticated user's account. (Aug 2026)
    const anonParams = useMemo(() => {
        const qs = new URLSearchParams(location.search);
        return {
            isAnonymous: qs.get('anon') === '1',
            deviceId: qs.get('device_id') || '',
        };
    }, [location.search]);
    const isAnonymous = anonParams.isAnonymous && id?.startsWith('bm_try_');

    // Smart back navigation: go to previous page if it was within the app, otherwise dashboard
    const goBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/dashboard');
        }
    }, [navigate]);
    const [bookmark, setBookmark] = useState(null);
    const [categories, setCategories] = useState([]);
    const parentCats = useMemo(() => categories.filter(c => !c.parent_id), [categories]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editTags, setEditTags] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [editCategoryIds, setEditCategoryIds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [editingNotes, setEditingNotes] = useState(false);
    const [inlineNotes, setInlineNotes] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingTranscript, setEditingTranscript] = useState(false);
    const [inlineTranscript, setInlineTranscript] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [noteImages, setNoteImages] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    // Last user question on the persisted Ask thread (if any). Drives
    // the "Continue: ..." contextual hint on the collapsed inline
    // trigger so revisits surface what was last asked.
    const [askLastQuestion, setAskLastQuestion] = useState('');
    const noteImageInputRef = React.useRef(null);
    const coverImageInputRef = React.useRef(null);
    const [recommendations, setRecommendations] = useState(null);
    const [recsLoading, setRecsLoading] = useState(false);
    const [activeReminder, setActiveReminder] = useState(null);

    // Poll for due reminders
    const { dueReminders, dismissReminder, snoozeReminder } = useReminderPolling(user);

    // Fetch the last user message on the Ask thread so the trigger pill
    // can render "Continue: <last question>" on revisits. Cheap call -
    // a single GET, no AI involvement. We refetch whenever bookmark.id
    // changes (i.e. navigation to a different bookmark page).
    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`/api/bookmarks/${id}/ask`, { credentials: 'include' });
                if (!res.ok || cancelled) return;
                const d = await res.json();
                const msgs = Array.isArray(d.messages) ? d.messages : [];
                const lastUser = [...msgs].reverse().find(m => m.role === 'user');
                if (!cancelled) setAskLastQuestion(lastUser?.content || '');
            } catch (_) { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [id]);

    // Auto-open the note editor when the user taps the Notes tab and
    // hasn't started a note yet. Mirrors what tapping "Add a note..."
    // does inline, so the tab affordance lands the user directly in the
    // textarea instead of forcing an extra tap on a dashed prompt.
    useEffect(() => {
        if (activeTab !== 'notes') return;
        if (editingNotes) return;
        const hasNote = !!(bookmark?.notes) || (noteImages && noteImages.length > 0);
        if (!hasNote) {
            startEditingNotes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, bookmark?.notes, noteImages?.length]);

    // Fetch active reminder for this bookmark
    useEffect(() => {
        if (!user || !id) return;
        fetch(`${API}/reminders`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : [])
            .then(reminders => {
                const match = reminders.find(r => r.bookmark_id === id);
                setActiveReminder(match || null);
            })
            .catch((err) => { console.error('Failed to load reminders:', err); });
    }, [user, id]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await fetch(`${API}/categories`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    }, []);

    const fetchBookmark = useCallback(async () => {
        try {
            const endpoint = isAnonymous
                ? `${API}/try/bookmark/${id}?device_id=${encodeURIComponent(anonParams.deviceId)}`
                : `${API}/bookmarks/${id}`;
            const response = await fetch(endpoint, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setBookmark(data);
                setEditTitle(data.title);
                setEditTags(data.tags?.join(', ') || '');
                setEditNotes(data.notes || '');
                setEditCategoryIds(data.category_ids || (data.category_id ? [data.category_id] : []));
                setNoteImages(data.note_images || []);
            } else if (response.status === 404 || response.status === 410) {
                toast.error(isAnonymous ? 'This preview has expired' : 'Bookmark not found');
                goBack();
            } else if (response.status === 401 && !isAnonymous) {
                const recovered = await checkAuth({ silent: true, maxRetries: 2 });
                if (!recovered) navigate('/');
            }
        } catch (error) {
            toast.error('Failed to load bookmark');
        } finally {
            setLoading(false);
        }
    }, [id, navigate, checkAuth, goBack, isAnonymous, anonParams.deviceId]);

    useEffect(() => {
        // Anonymous try-it preview — no auth required, just fetch.
        // If the user completes sign-in mid-preview (e.g. tapped
        // "Keep this forever" and finished OAuth), the `user` state
        // flips populated. The pending-claim helper in AuthContext
        // has already migrated the bookmark to their real account,
        // so send them to /dashboard — that's where onboarding lives
        // and where the just-claimed bookmark now sits at the top of
        // their library. (Product decision revised Aug 24 2026:
        // previously we redirected to /bookmark/{id}, but the user
        // wanted new sign-ups to see their new library home first.)
        if (isAnonymous) {
            if (user && !authLoading) {
                navigate('/dashboard', { replace: true });
                return;
            }
            fetchBookmark();
            return;
        }
        if (!authLoading && !user) {
            checkAuth({ silent: false, maxRetries: 2 }).then(recovered => {
                if (!recovered) navigate('/');
            });
            return;
        }
        if (user) {
            fetchBookmark();
            fetchCategories();
        }
    }, [user, authLoading, fetchBookmark, fetchCategories, navigate, checkAuth, isAnonymous, id]);

    // Poll if still processing
    useEffect(() => {
        if (bookmark?.status === 'processing' || bookmark?.status === 'pending') {
            const interval = setInterval(fetchBookmark, 5000);
            return () => clearInterval(interval);
        }
    }, [bookmark?.status, fetchBookmark]);

    // Fetch recommendations when bookmark is loaded
    useEffect(() => {
        if (bookmark?.status === 'completed' && bookmark?.tags?.length > 0 && !recommendations) {
            setRecsLoading(true);
            fetch(`${API}/bookmarks/${id}/recommendations`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setRecommendations(data); })
                .catch((err) => { console.error('Failed to load recommendations:', err); })
                .finally(() => setRecsLoading(false));
        }
    }, [bookmark?.status, bookmark?.tags, id, recommendations]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(`${API}/bookmarks/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    title: editTitle,
                    tags: editTags.split(',').map(t => t.trim()).filter(Boolean),
                    notes: editNotes
                })
            });

            if (response.ok) {
                const updated = await response.json();
                setBookmark(updated);
                setEditing(false);
                toast.success('Changes saved');
            } else {
                toast.error('Failed to save changes');
            }
        } catch (error) {
            toast.error('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const toggleCategory = async (catId) => {
        const prev = editCategoryIds;
        const newIds = prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId];
        setEditCategoryIds(newIds);
        // Auto-save immediately
        try {
            const response = await fetch(`${API}/bookmarks/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ category_ids: newIds })
            });
            if (response.ok) {
                const updated = await response.json();
                setBookmark(updated);
            }
        } catch (_) { /* silent */ }
    };

    const startEditingNotes = () => {
        setInlineNotes(bookmark.notes || '');
        setEditingNotes(true);
    };

    const saveNotes = async () => {
        try {
            const response = await fetch(`${API}/bookmarks/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ notes: inlineNotes })
            });
            if (response.ok) {
                const updated = await response.json();
                setBookmark(updated);
                setEditingNotes(false);
                toast.success('Note saved');
            } else {
                toast.error('Failed to save note');
            }
        } catch (error) {
            toast.error('Failed to save note');
        }
    };

    const uploadImageToNotes = async (file) => {
        if (!file) return;
        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${API}/images/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if (response.ok) {
                const { url } = await response.json();
                const fullUrl = url;
                const updated = [...noteImages, fullUrl];
                setNoteImages(updated);
                // Persist immediately
                await fetch(`${API}/bookmarks/${id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ note_images: updated })
                });
                setBookmark(prev => prev ? { ...prev, note_images: updated } : prev);
                toast.success('Photo added');
            } else {
                toast.error('Failed to upload photo');
            }
        } catch (error) {
            toast.error('Failed to upload photo');
        } finally {
            setUploadingImage(false);
        }
    };

    const deleteNoteImage = async (index) => {
        const updated = noteImages.filter((_, i) => i !== index);
        setNoteImages(updated);
        try {
            await fetch(`${API}/bookmarks/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ note_images: updated })
            });
            setBookmark(prev => prev ? { ...prev, note_images: updated } : prev);
            toast.success('Photo removed');
        } catch {
            toast.error('Failed to remove photo');
        }
    };

    const changeCoverPhoto = async (file) => {
        if (!file) return;
        setUploadingCover(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${API}/images/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if (response.ok) {
                const { url } = await response.json();
                const fullUrl = url;
                const patchResp = await fetch(`${API}/bookmarks/${id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ thumbnail: fullUrl })
                });
                if (patchResp.ok) {
                    const updated = await patchResp.json();
                    setBookmark(updated);
                    toast.success('Cover photo updated');
                } else {
                    toast.error('Failed to update cover photo');
                }
            } else {
                toast.error('Failed to upload photo');
            }
        } catch (error) {
            toast.error('Failed to change cover photo');
        } finally {
            setUploadingCover(false);
        }
    };

    const startEditingTranscript = () => {
        setInlineTranscript(bookmark.transcript || '');
        setEditingTranscript(true);
    };

    const [savingTranscript, setSavingTranscript] = useState(false);

    const saveTranscript = async () => {
        setSavingTranscript(true);
        try {
            const response = await fetch(`${API}/bookmarks/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ transcript: inlineTranscript })
            });
            if (response.ok) {
                const updated = await response.json();
                setBookmark(updated);
                setEditingTranscript(false);
                toast.success('Saved! Generating new AI summary...');
            } else {
                toast.error('Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save');
        } finally {
            setSavingTranscript(false);
        }
    };

    const handleReprocess = async () => {
        try {
            const response = await fetch(`${API}/bookmarks/${id}/reprocess`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Reprocessing started');
                fetchBookmark();
            } else if (response.status === 402) {
                // Premium AI quota exhausted - guide the user to upgrade
                let detail = {};
                try { detail = (await response.json())?.detail || {}; } catch (_) { /* ignore */ }
                toast.error(detail.message || 'You\'re out of Premium AI saves. Upgrade to reprocess.');
            } else {
                toast.error('Failed to start reprocessing');
            }
        } catch (error) {
            toast.error('Failed to start reprocessing');
        }
    };

    const handleDelete = async () => {
        try {
            const response = await fetch(`${API}/bookmarks/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Bookmark deleted');
                // Signal Dashboard to drop this ID from its restored/cached list
                // (otherwise the thumbnail lingers until a full refresh).
                try {
                    const existing = JSON.parse(sessionStorage.getItem('dashboard_deleted_ids') || '[]');
                    if (!existing.includes(id)) existing.push(id);
                    sessionStorage.setItem('dashboard_deleted_ids', JSON.stringify(existing));
                } catch (e) { /* storage full - refetch will still fix it */ }
                goBack();
            } else {
                toast.error('Failed to delete bookmark');
            }
        } catch (error) {
            toast.error('Failed to delete bookmark');
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    const handleCopy = async (text) => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
    };

    const [shareModalOpen, setShareModalOpen] = useState(false);

    const handleShare = () => {
        // New sharing UX: full modal with privacy + channels + referral link
        setShareModalOpen(true);
    };

    const handleQuickShare = async () => {
        // Share strategy (same as Dashboard's handler):
        //   1) navigator.share - Chrome browser / Safari / modern PWAs.
        //   2) window.BrainSyncNative.share - our APK's JS→native
        //      bridge (LauncherActivity.java → ShareBridge) that
        //      fires Android's real share chooser. Only present in
        //      the wrapped Android app.
        //   3) clipboard copy - universal fallback.
        if (!bookmark) return;
        // Share URL points to the backend SSR endpoint
        // (`/api/share/:id`) - not the React-only `/share/:id` route.
        // The backend dynamically generates bookmark-specific OG meta
        // tags for crawlers (WhatsApp / Twitter / Facebook etc.) and
        // 302-redirects real browsers to `/share/:id` for the full
        // React experience. Pointing share URLs directly at `/share/:id`
        // breaks link previews because crawlers receive the generic
        // index.html with the app-level icon, not the bookmark image.
        const shareUrl = `${window.location.origin}/api/share/${bookmark.bookmark_id || id}`;
        const shareTitle = bookmark.title || 'Bookmark';
        try {
            await navigator.share({ title: shareTitle, text: shareTitle, url: shareUrl });
            return;
        } catch (err) {
            if (err?.name === 'AbortError') return;
            try {
                if (window.BrainSyncNative && typeof window.BrainSyncNative.share === 'function') {
                    window.BrainSyncNative.share(`${shareTitle}\n${shareUrl}`);
                    return;
                }
            } catch (_) { /* fall through */ }
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success('Link copied - paste it anywhere');
            } catch (_) {
                toast.error('Could not copy link');
            }
        }
    };

    const handleToggleFavorite = async () => {
        try {
            const response = await fetch(`${API}/bookmarks/${id}/favorite`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setBookmark(prev => prev ? { ...prev, is_favorite: data.is_favorite } : prev);
            }
        } catch (error) {
            toast.error('Failed to update favorite');
        }
    };

    const getContentIcon = (contentType) => {
        switch (contentType) {
            case 'video': return <Play className="w-5 h-5" />;
            case 'image': return <ImageIcon className="w-5 h-5" />;
            default: return <FileText className="w-5 h-5" />;
        }
    };

    const getStatusBadge = (status) => {
        const stage = bookmark?.processing_stage;
        const stageLabels = { extracting: 'Reviewing content', downloading: 'Reviewing content', transcribing: 'Transcribing audio', analyzing: 'AI analysis', enriching: 'Finishing touches' };
        switch (status) {
            case 'pending':
                return <Badge variant="secondary" className="font-mono text-sm"><Clock className="w-4 h-4 mr-1" /> Pending</Badge>;
            case 'processing':
                return (
                    <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="font-mono text-sm bg-accent text-accent-foreground">
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" /> {stage ? stageLabels[stage] || 'Processing' : 'Processing'}
                        </Badge>
                        <ProcessingSteps stage={stage || 'extracting'} />
                    </div>
                );
            case 'failed':
                return <Badge variant="destructive" className="font-mono text-sm">Failed</Badge>;
            case 'completed':
            default:
                return null;
        }
    };

    const isHebrew = (text) => {
        if (!text) return false;
        const hebrewChars = (text.match(/[\u0590-\u05FF]/g) || []).length;
        const latinChars = (text.match(/[a-zA-Z]/g) || []).length;
        return hebrewChars > latinChars;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateShort = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getDomain = (url) => {
        try { return new URL(url).hostname.replace('www.', ''); } catch { return url; }
    };

    const getCategoryById = (categoryId) => {
        return categories.find(c => c.category_id === categoryId);
    };

    const getCategoryIcon = (iconName) => {
        return CATEGORY_ICONS[iconName] || Folder;
    };

    const hasQualityIssues = bookmark?.quality_issues && bookmark.quality_issues.length > 0
        && !bookmark?.summary
        && !(bookmark?.content_subtype === 'place' && bookmark?.place_data);

    const getQualityMessage = (issues) => {
        if (!issues || issues.length === 0) return null;
        const messages = [];
        if (issues.includes('login_page_content')) messages.push('The content appears to be a login or navigation page, not the actual content you intended to save.');
        if (issues.includes('no_transcript')) messages.push('No audio transcript could be extracted from this video.');
        if (issues.includes('no_summary')) messages.push('A summary could not be generated for this content.');
        if (issues.includes('generic_title')) messages.push('The title appears generic and may not reflect the actual content.');
        return messages.length > 0 ? messages : ['Some content may not have been extracted properly.'];
    };

    // Auto-link URLs and domain names in text
    const linkifyText = (text) => {
        // Match full URLs or bare domains (e.g., notion.com, stripe.com/pricing)
        const urlRegex = /(https?:\/\/[^\s,)]+)|(\b[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}(?:\/[^\s,)]*)?)/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        while ((match = urlRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }
            const raw = match[0];
            const href = raw.startsWith('http') ? raw : `https://${raw}`;
            parts.push(
                <a key={match.index} href={href} target="_blank" rel="noopener noreferrer"
                   onClick={(e) => e.stopPropagation()}
                   className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors font-medium">
                    {raw}
                </a>
            );
            lastIndex = match.index + raw.length;
        }
        if (lastIndex < text.length) parts.push(text.slice(lastIndex));
        return parts.length > 1 ? parts : text;
    };

    // Parse and format summary with bullet points
    const renderSummary = (summary) => {
        if (!summary) return null;
        
        const lines = summary.split('\n').filter(line => line.trim());
        
        return (
            <div className="space-y-4">
                {lines.map((line, index) => {
                    const trimmedLine = line.trim();
                    const lineDir = isHebrew(trimmedLine) ? 'rtl' : 'ltr';
                    const lineKey = `line-${index}-${trimmedLine.slice(0, 20).replace(/\s/g, '')}`;
                    
                    // Check if it's a bullet point
                    if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
                        const content = trimmedLine.replace(/^[•\-*]\s*/, '');
                        return (
                            <div key={lineKey} className="flex items-start gap-3 pl-2" dir={isHebrew(content) ? 'rtl' : 'ltr'}>
                                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                                <p className="text-foreground/80 leading-relaxed">{linkifyText(content)}</p>
                            </div>
                        );
                    }
                    
                    // Check if it's the "Why this matters" section
                    const whyMattersMatch = trimmedLine.match(/^(WHY THIS MATTERS|Why this matters|למה זה חשוב)[:\s]*(.*)/i);
                    if (whyMattersMatch) {
                        const content = whyMattersMatch[2] || '';
                        return (
                            <div key={lineKey} className="mt-6 p-5 bg-gradient-to-r from-primary/10 to-accent/20 rounded-2xl border border-primary/20" dir={isHebrew(content || whyMattersMatch[1]) ? 'rtl' : 'ltr'}>
                                <p className="text-xs font-mono uppercase tracking-wider text-primary mb-2 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4" />
                                    {whyMattersMatch[1]}
                                </p>
                                <p className="text-foreground font-medium leading-relaxed">{content}</p>
                            </div>
                        );
                    }
                    
                    // Check if it's an "English Summary" header for bilingual content
                    if (trimmedLine.match(/^(English Summary|סיכום באנגלית)[:\s]*$/i)) {
                        return (
                            <div key={lineKey} className="mt-8 pt-6 border-t border-border">
                                <p className="text-sm font-semibold text-primary mb-4 flex items-center gap-2" dir={lineDir}>
                                    {trimmedLine.replace(':', '')}
                                </p>
                            </div>
                        );
                    }
                    
                    // Check if it's a header-like line (short and followed by colon)
                    if (trimmedLine.includes(':') && trimmedLine.length < 60 && !trimmedLine.match(/^(http|www)/i)) {
                        const colonIndex = trimmedLine.indexOf(':');
                        const title = trimmedLine.substring(0, colonIndex).trim();
                        const content = trimmedLine.substring(colonIndex + 1).trim();
                        
                        // Skip if it looks like "Why this matters" without content on same line
                        if (title.toLowerCase().includes('why this matters') || title.includes('למה זה חשוב') || title.includes('Почему это важно')) {
                            return (
                                <div key={lineKey} className="mt-6 p-5 bg-gradient-to-r from-primary/10 to-accent/20 rounded-2xl border border-primary/20" dir={isHebrew(content || title) ? 'rtl' : 'ltr'}>
                                    <p className="text-xs font-mono uppercase tracking-wider text-primary mb-2 flex items-center gap-2">
                                        <Sparkles className="w-4 h-4" />
                                        {title}
                                    </p>
                                    {content && <p className="text-foreground font-medium leading-relaxed">{content}</p>}
                                </div>
                            );
                        }
                        
                        if (content) {
                            return (
                                <div key={lineKey} className="bg-secondary/50 rounded-xl p-4" dir={isHebrew(content) ? 'rtl' : 'ltr'}>
                                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">{title}</p>
                                    <p className="text-foreground/80 leading-relaxed">{linkifyText(content)}</p>
                                </div>
                            );
                        }
                        return (
                            <p key={lineKey} className="text-sm font-semibold text-primary mt-4" dir={lineDir}>{trimmedLine}</p>
                        );
                    }
                    
                    // Regular paragraph
                    return (
                        <p key={lineKey} className="text-foreground/80 leading-relaxed" dir={lineDir}>{linkifyText(trimmedLine)}</p>
                    );
                })}
            </div>
        );
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!bookmark) {
        return null;
    }

    return (
        <div className="min-h-screen bg-background overflow-x-hidden" data-testid="bookmark-detail">
            <Toaster position="top-right" richColors />
            <ReminderAlerts dueReminders={dueReminders} onDismiss={dismissReminder} onSnooze={snoozeReminder} />

            {/* Header */}
            <header className="sticky top-0 z-50 glass border-b border-border/40">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <a href="/" onClick={(e) => { e.preventDefault(); goBack(); }} data-testid="header-logo-detail">
                            <div className="w-8 h-8 rounded-lg overflow-hidden shadow-sm">
                                <img src="/icon-192.png" alt="BrainSync" className="w-full h-full object-cover" />
                            </div>
                        </a>
                        <Button 
                            variant="ghost" 
                            onClick={goBack}
                            className="rounded-full px-2 sm:px-3"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5 sm:mr-2" />
                            <span className="hidden sm:inline">Back</span>
                        </Button>
                    </div>

                    <div className="flex items-center gap-1 sm:gap-2">
                        {isAnonymous ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-mono uppercase tracking-widest" style={{ background: '#E3F1E9', color: '#1F7A55' }} data-testid="anon-preview-pill">
                                <Sparkles className="w-3 h-3" /> Preview
                            </span>
                        ) : editing ? (
                            <>
                                <Button 
                                    variant="ghost" 
                                    onClick={() => setEditing(false)}
                                    className="rounded-full px-2 sm:px-3"
                                >
                                    <X className="w-4 h-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Cancel</span>
                                </Button>
                                <Button 
                                    onClick={handleSave}
                                    className="rounded-full px-2 sm:px-3"
                                    disabled={saving}
                                    data-testid="save-btn"
                                >
                                    {saving ? (
                                        <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 sm:mr-2" />
                                    )}
                                    <span className="hidden sm:inline">Save</span>
                                </Button>
                            </>
                        ) : (
                            <>
                                {/* Privacy status pill - replaces the previous
                                    Favorite + Share buttons up here. Clicking
                                    it opens the existing full ShareModal (where
                                    privacy can be changed). Favorite + share
                                    actions moved down beside the cover image
                                    for thumb-friendly access. */}
                                {(() => {
                                    // Backend default is 'unlisted' (see
                                    // routes/share.py `share_visibility` fallbacks).
                                    // Falling back to 'private' here showed every
                                    // newly-saved bookmark as Private even though
                                    // it was actually Unlisted, which was wrong.
                                    const ownVis = bookmark.share_visibility || 'unlisted';

                                    // Effective visibility - the backend
                                    // (routes/profiles.py) exposes a bookmark on
                                    // the public profile if EITHER its own
                                    // share_visibility=public OR it sits in a
                                    // public category. `private` is the only
                                    // safety override: a private bookmark stays
                                    // hidden even inside a public category
                                    // (categories.py:369). So the pill should
                                    // reflect that inheritance - otherwise a
                                    // bookmark inside a "Public: Bangkok"
                                    // category reads as "Unlisted" and misleads
                                    // the owner about what's actually exposed.
                                    const publicCategory = ownVis !== 'private' && Array.isArray(bookmark.category_ids)
                                        ? categories.find(c => bookmark.category_ids.includes(c.category_id) && c.share_visibility === 'public')
                                        : null;

                                    const variants = {
                                        public_via_category: {
                                            label: `Public · via ${publicCategory?.name || 'collection'}`,
                                            icon: Globe,
                                            className: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
                                            aria: `Privacy: Public, inherited from collection ${publicCategory?.name || ''}. Click to change.`,
                                        },
                                        public:   { label: 'Public',   icon: Globe,  className: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100', aria: 'Privacy: Public. Click to change.' },
                                        unlisted: { label: 'Unlisted', icon: Eye,    className: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',         aria: 'Privacy: Unlisted. Click to change.' },
                                        private:  { label: 'Private',  icon: Lock,   className: 'bg-zinc-100 text-zinc-700 border-zinc-200 hover:bg-zinc-200',           aria: 'Privacy: Private. Click to change.' },
                                    };
                                    const v = publicCategory ? variants.public_via_category : (variants[ownVis] || variants.private);
                                    const IconV = v.icon;
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => setShareModalOpen(true)}
                                            className={`inline-flex items-center justify-center rounded-full w-8 h-8 border transition-colors ${v.className}`}
                                            data-testid="bookmark-privacy-pill"
                                            aria-label={v.aria}
                                            title={v.label}
                                        >
                                            <IconV className="w-4 h-4" />
                                        </button>
                                    );
                                })()}
                                <Button 
                                    variant="ghost" 
                                    onClick={() => setEditing(true)}
                                    className="rounded-full px-2 sm:px-3"
                                    data-testid="edit-btn"
                                >
                                    <Edit2 className="w-4 h-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Edit</span>
                                </Button>
                                <ReminderPicker
                                    bookmarkId={id}
                                    bookmarkTitle={bookmark.title}
                                    existingReminder={activeReminder}
                                    onReminderChange={setActiveReminder}
                                />
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="rounded-full px-2" data-testid="more-actions-btn">
                                            <MoreVertical className="w-4 h-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={handleReprocess}>
                                            <RefreshCw className="w-4 h-4 mr-2" /> Reprocess
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} className="text-destructive">
                                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {/* Quality Issue Banner */}
                    {hasQualityIssues && (
                        <div 
                            className="mb-6 p-4 sm:p-5 rounded-2xl bg-amber-50 border border-amber-200/60"
                            data-testid="quality-issue-banner"
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-heading font-semibold text-amber-900 text-sm mb-1">Content extraction issues detected</h3>
                                    <ul className="space-y-1">
                                        {getQualityMessage(bookmark.quality_issues).map((msg, i) => (
                                            <li key={`quality-${msg.slice(0, 15)}`} className="text-sm text-amber-800 leading-relaxed flex items-start gap-2">
                                                <span className="w-1 h-1 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                                                {msg}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="text-xs text-amber-600 mt-2">This can happen when a site requires login or blocks content extraction.</p>
                                </div>
                                <Button
                                    onClick={handleReprocess}
                                    className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0 shadow-sm"
                                    data-testid="quality-reprocess-btn"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Reprocess
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Title Section */}
                    <div className="mb-4">
                        <div className="flex items-center gap-3 mb-2">
                            {hasQualityIssues && (
                                <Badge variant="secondary" className="font-mono text-sm bg-amber-100 text-amber-700">
                                    <AlertTriangle className="w-4 h-4 mr-1" /> Needs Review
                                </Badge>
                            )}
                            {!hasQualityIssues && getStatusBadge(bookmark.status)}
                        </div>

                        {editing ? (
                            <Input
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="text-3xl font-heading font-bold py-4 px-0 border-0 border-b rounded-none focus:ring-0"
                                data-testid="edit-title-input"
                            />
                        ) : (
                            <>
                            <h1 
                                className="font-heading text-xl sm:text-2xl md:text-3xl font-bold tracking-tight leading-snug"
                                dir={isHebrew(bookmark.title) ? 'rtl' : 'ltr'}
                            >
                                {bookmark.title}
                            </h1>
                            {editCategoryIds.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2" dir={isHebrew(bookmark.title) ? 'rtl' : 'ltr'}>
                                    {editCategoryIds.map(catId => {
                                        const cat = getCategoryById(catId);
                                        if (!cat) return null;
                                        const IconComponent = getCategoryIcon(cat.icon);
                                        return (
                                            <span key={catId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium font-body border" style={{ borderColor: cat.color, color: cat.color, backgroundColor: `${cat.color}10` }}>
                                                <IconComponent className="w-3 h-3" />
                                                {cat.name}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            </>
                        )}

                        {/* Compact meta line: domain · date */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                            {bookmark.url ? (
                                <a 
                                    href={bookmark.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 hover:text-primary transition-colors"
                                >
                                    <LinkIcon className="w-3 h-3" />
                                    <span className="truncate max-w-[180px] sm:max-w-xs">{getDomain(bookmark.url)}</span>
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            ) : (
                                <span className="inline-flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    Manual note
                                </span>
                            )}
                            {bookmark.author && (
                                <>
                                    <span className="text-border">·</span>
                                    <span className="inline-flex items-center gap-1" data-testid="bookmark-author">
                                        <User className="w-3 h-3" />
                                        {bookmark.author}
                                    </span>
                                </>
                            )}
                            <span className="text-border">·</span>
                            <span className="font-mono">{formatDateShort(bookmark.created_at)}</span>
                            {bookmark.updated_at && (() => {
                                const created = new Date(bookmark.created_at).getTime();
                                const updated = new Date(bookmark.updated_at).getTime();
                                return (updated - created) > 60000;
                            })() && (
                                <>
                                    <span className="text-border">·</span>
                                    <span className="font-mono">Edited {formatDateShort(bookmark.updated_at)}</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Collections - edit mode, auto-saves on toggle */}
                    {editing && (
                        <div className="mb-4">
                            <label className="text-sm font-medium text-muted-foreground mb-2 block flex items-center gap-2">
                                <Bookmark className="w-4 h-4" />
                                Collections
                            </label>
                            <div className="flex flex-wrap gap-1.5" data-testid="category-chips">
                                {parentCats.map((cat) => {
                                    const IconComponent = getCategoryIcon(cat.icon);
                                    const isSelected = editCategoryIds.includes(cat.category_id);
                                    const subs = categories.filter(s => s.parent_id === cat.category_id);
                                    return (
                                        <React.Fragment key={cat.category_id}>
                                            <button type="button" onClick={() => toggleCategory(cat.category_id)}
                                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border cursor-pointer ${isSelected ? 'bg-primary/10 text-primary border-primary' : 'bg-transparent text-muted-foreground border-border hover:bg-secondary/50'}`}>
                                                {isSelected && <Check className="w-3 h-3" />}
                                                <IconComponent className="w-3 h-3" style={{ color: isSelected ? undefined : cat.color }} />
                                                {cat.name}
                                            </button>
                                            {subs.map(sub => {
                                                const isSubSel = editCategoryIds.includes(sub.category_id);
                                                return (
                                                    <button key={sub.category_id} type="button" onClick={() => toggleCategory(sub.category_id)}
                                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all border cursor-pointer ${isSubSel ? 'bg-primary/10 text-primary border-primary' : 'bg-transparent text-muted-foreground border-border hover:bg-secondary/50'}`}>
                                                        {isSubSel && <Check className="w-3 h-3" />}
                                                        <ChevronRight className="w-2.5 h-2.5" />{sub.name}
                                                    </button>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Tags - edit mode only */}
                    {editing && (
                        <div className="mb-6">
                            <label className="text-sm font-medium text-muted-foreground mb-2 block flex items-center gap-2">
                                <Tag className="w-4 h-4" />
                                Tags
                            </label>
                            <Input
                                value={editTags}
                                onChange={(e) => setEditTags(e.target.value)}
                                placeholder="ai, tech, learning (comma separated)"
                                className="rounded-xl max-w-md"
                                data-testid="edit-tags-input"
                            />
                        </div>
                    )}

                    {/* Buy on Amazon - visible when AI vision detected a retail product in the bookmark image */}
                    {bookmark.product_info?.is_product && bookmark.product_info?.product_name && (() => {
                        const url = buildAmazonSearchUrl({
                            productName: bookmark.product_info.product_name,
                            brand: bookmark.product_info.brand,
                        });
                        if (!url) return null;
                        return (
                            <div className="mb-6" data-testid="amazon-action-links">
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                    Buy this product
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 hover:border-orange-400 hover:shadow-md transition-all text-sm group"
                                        data-testid="amazon-buy-link"
                                    >
                                        <span className="w-6 h-6 rounded-full bg-white border border-orange-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                            <ShoppingBag className="w-3.5 h-3.5 text-orange-600" />
                                        </span>
                                        <span className="text-orange-900 font-medium">
                                            {bookmark.product_info.brand
                                                ? `${bookmark.product_info.brand} · ${bookmark.product_info.product_name}`
                                                : bookmark.product_info.product_name}
                                        </span>
                                        <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-[#ff9900] text-black text-[10px] font-bold">
                                            Amazon
                                        </span>
                                    </a>

                                    {/* Google Lens - visual search using the bookmark's image.
                                        Accepts both absolute URLs (videos, scraped articles) and
                                        relative `/api/...` paths (manual uploads). Google Lens
                                        fetches the image server-side via uploadbyurl, so we must
                                        always pass an absolute URL. */}
                                    {bookmark.thumbnail && (() => {
                                        const t = bookmark.thumbnail;
                                        const absUrl = /^https?:\/\//.test(t)
                                            ? t
                                            : (typeof window !== 'undefined' ? `${window.location.origin}${t.startsWith('/') ? t : `/${t}`}` : null);
                                        if (!absUrl) return null;
                                        return (
                                            <a
                                                href={`https://lens.google.com/uploadbyurl?url=${encodeURIComponent(absUrl)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all text-sm group"
                                                data-testid="google-lens-link"
                                                title="Visual search across the web - including other retailers, eBay, AliExpress, etc."
                                            >
                                                <span className="w-6 h-6 rounded-full bg-white border border-blue-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                                    <Search className="w-3.5 h-3.5 text-blue-600" />
                                                </span>
                                                <span className="text-blue-900 font-medium">Find similar</span>
                                                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">
                                                    Google Lens
                                                </span>
                                            </a>
                                        );
                                    })()}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Business Action Buttons - Google Maps, Booking.com */}
                    {/* Individual places mentioned */}
                    {bookmark.places_mentioned && bookmark.places_mentioned.length > 0 && (
                        <div className="mb-6" data-testid="business-action-links">                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                Places Mentioned
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {bookmark.places_mentioned.map((place, i) => (
                                    <a
                                        key={place.name || i}
                                        href={place.google_maps_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 hover:border-green-400 hover:shadow-md transition-all text-sm group"
                                        data-testid={`place-maps-link-${i}`}
                                    >
                                        <span className="w-6 h-6 rounded-full bg-white border border-green-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                            <MapPin className="w-3.5 h-3.5 text-green-600" />
                                        </span>
                                        <span className="text-green-800 font-medium">{place.name}</span>
                                        {place.booking_url && (
                                            <span
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(place.booking_url, '_blank'); }}
                                                className="ml-1 px-2 py-0.5 rounded-full bg-[#003580] text-white text-[10px] font-bold hover:bg-[#00264d] cursor-pointer"
                                                data-testid={`place-booking-link-${i}`}
                                            >
                                                Book
                                            </span>
                                        )}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Primary business - single Maps + Booking buttons (when no places list) */}
                    {!bookmark.places_mentioned?.length && (bookmark.google_maps_url || bookmark.booking_url) && (
                        <div className="mb-6" data-testid="business-action-links">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                Places Mentioned
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {bookmark.google_maps_url && (
                                    <a
                                        href={bookmark.google_maps_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 hover:border-green-400 hover:shadow-md transition-all text-sm group"
                                        data-testid="google-maps-link"
                                    >
                                        <span className="w-6 h-6 rounded-full bg-white border border-green-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                            <MapPin className="w-3.5 h-3.5 text-green-600" />
                                        </span>
                                        <span className="text-green-800 font-medium">
                                            {bookmark.business_info?.business_name || 'View on Google Maps'}
                                        </span>
                                        {bookmark.booking_url && (
                                            <span
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(bookmark.booking_url, '_blank'); }}
                                                className="ml-1 px-2 py-0.5 rounded-full bg-[#003580] text-white text-[10px] font-bold hover:bg-[#00264d] cursor-pointer"
                                                data-testid="booking-link"
                                            >
                                                Book
                                            </span>
                                        )}
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Movies / TV Shows - IMDB Links */}
                    {bookmark.media_mentioned && bookmark.media_mentioned.length > 0 && (
                        <div className="mb-6" data-testid="media-action-links">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                Movies & TV Shows
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {bookmark.media_mentioned.map((item, i) => (
                                    <a
                                        key={item.title || i}
                                        href={item.imdb_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 hover:border-amber-400 hover:shadow-md transition-all text-sm group"
                                        data-testid={`media-imdb-link-${i}`}
                                    >
                                        <span className="w-6 h-6 rounded-full bg-white border border-amber-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                            <Film className="w-3.5 h-3.5 text-amber-600" />
                                        </span>
                                        <span className="text-amber-900 font-medium">{item.title}</span>
                                        {item.episode_title && (
                                            <span className="text-amber-700 text-xs italic" data-testid={`media-episode-title-${i}`}>
                                                - {item.episode_title}
                                            </span>
                                        )}
                                        {item.year && (
                                            <span className="text-amber-500 text-xs">({item.year})</span>
                                        )}
                                        <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-[#f5c518] text-black text-[10px] font-bold">
                                            IMDb
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Books - Goodreads & Amazon Links */}
                    {bookmark.books_mentioned && bookmark.books_mentioned.length > 0 && (
                        <div className="mb-6" data-testid="books-action-links">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                Books Mentioned
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {bookmark.books_mentioned.map((book, i) => (
                                    <a
                                        key={book.title || i}
                                        href={book.goodreads_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 hover:border-sky-400 hover:shadow-md transition-all text-sm group"
                                        data-testid={`book-goodreads-link-${i}`}
                                    >
                                        <span className="w-6 h-6 rounded-full bg-white border border-sky-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                            <BookOpen className="w-3.5 h-3.5 text-sky-600" />
                                        </span>
                                        <span className="text-sky-900 font-medium">{book.title}</span>
                                        {book.author && (
                                            <span className="text-sky-500 text-xs">by {book.author}</span>
                                        )}
                                        <span
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(book.amazon_url, '_blank'); }}
                                            className="ml-0.5 px-1.5 py-0.5 rounded-full bg-[#FF9900] text-black text-[10px] font-bold hover:bg-[#e68a00] cursor-pointer"
                                            data-testid={`book-amazon-link-${i}`}
                                        >
                                            Amazon
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Media Player - YouTube / Spotify / Apple Music
                        / SoundCloud inline embed (returns null for
                        non-embeddable URLs, in which case the
                        thumbnail block below renders instead). */}
                    <InlineMediaPlayer
                        url={bookmark.url}
                        spotifyEmbedUrl={bookmark.spotify_embed_url}
                        tiktokVideoId={bookmark.tiktok_video_id}
                        title={bookmark.title}
                        className="mb-8"
                        testIdPrefix="bookmark"
                    />

                    {/* Thumbnail with play button for videos */}
                    {!(() => {
                        // Hide the click-thumbnail when we already have
                        // an inline player above (covers YouTube,
                        // Spotify, Apple Music, TikTok, SoundCloud).
                        if (isEmbeddableMediaUrl(bookmark.url, bookmark.spotify_embed_url, bookmark.tiktok_video_id)) return true;
                        try {
                            const u = new URL(bookmark.url || '');
                            return u.hostname.includes('youtube') || u.hostname.includes('youtu.be') || u.hostname.includes('open.spotify.com');
                        } catch (e) { return false; }
                    })() && bookmark.thumbnail && (
                        <div className="mb-8 rounded-2xl overflow-hidden border border-border/60 relative group">
                            <img 
                                src={bookmark.thumbnail} 
                                alt={bookmark.title}
                                className="w-full max-h-96 object-cover"
                            />
                            {/* Floating action buttons on the cover - Favorite
                                + Quick Share. Mirrors the placement used on
                                the dashboard card so the affordance is
                                consistent between list and detail views.
                                Click handlers stop propagation so they don't
                                trigger the underlying video play link. */}
                            <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleToggleFavorite(); }}
                                    className="w-9 h-9 rounded-full bg-white/95 backdrop-blur hover:bg-white flex items-center justify-center shadow-md transition-transform active:scale-90"
                                    data-testid="cover-favorite-btn"
                                    aria-label={bookmark.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                                >
                                    <Heart className={`w-4 h-4 transition-colors ${bookmark.is_favorite ? 'fill-red-500 text-red-500' : 'text-zinc-700'}`} />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleQuickShare(); }}
                                    className="w-9 h-9 rounded-full bg-white/95 backdrop-blur hover:bg-white flex items-center justify-center shadow-md transition-transform active:scale-90"
                                    data-testid="cover-share-btn"
                                    aria-label="Share bookmark"
                                >
                                    <Share2 className="w-4 h-4 text-zinc-700" />
                                </button>
                            </div>
                            {bookmark.content_type === 'video' && bookmark.url && (
                                <a
                                    href={bookmark.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="absolute inset-0 flex items-center justify-center"
                                    data-testid="video-play-link"
                                >
                                    <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-sm hover:bg-black/80 transition-colors">
                                        <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                                    </div>
                                </a>
                            )}
                            <button
                                onClick={() => coverImageInputRef.current?.click()}
                                className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 text-white text-xs font-medium sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-black/80 backdrop-blur-sm"
                                disabled={uploadingCover}
                                data-testid="change-cover-btn"
                            >
                                {uploadingCover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                Change cover
                            </button>
                            <input
                                ref={coverImageInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => { if (e.target.files?.[0]) changeCoverPhoto(e.target.files[0]); e.target.value = ''; }}
                            />
                        </div>
                    )}
                    {/* Add cover when none exists */}
                    {!(() => {
                        try {
                            const u = new URL(bookmark.url || '');
                            return u.hostname.includes('youtube') || u.hostname.includes('youtu.be');
                        } catch (e) { return false; }
                    })() && !bookmark.thumbnail && (
                        <button
                            onClick={() => coverImageInputRef.current?.click()}
                            className="mb-8 w-full flex items-center justify-center gap-2 py-6 rounded-2xl border border-dashed border-border hover:border-primary/30 hover:bg-secondary/30 transition-colors text-sm text-muted-foreground"
                            disabled={uploadingCover}
                            data-testid="add-cover-btn"
                        >
                            {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                            Add cover photo
                            <input
                                ref={coverImageInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => { if (e.target.files?.[0]) changeCoverPhoto(e.target.files[0]); e.target.value = ''; }}
                            />
                        </button>
                    )}

                    {/* Tabs for Content */}
                    <Tabs
                        defaultValue={
                            bookmark.content_subtype === 'song' ? 'song'
                                : bookmark.place_data ? 'place'
                                : bookmark.recipe_data ? 'recipe'
                                : 'summary'
                        }
                        className="w-full"
                        onValueChange={setActiveTab}
                    >
                        <TabsList className={`grid w-full bg-secondary rounded-xl p-1 mb-6 ${(bookmark.recipe_data || bookmark.place_data || bookmark.content_subtype === 'song') ? 'grid-cols-4' : 'grid-cols-3'}`}>
                            {bookmark.content_subtype === 'song' && (
                                <TabsTrigger value="song" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-song">
                                    <Music className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Song</span>
                                </TabsTrigger>
                            )}
                            {bookmark.recipe_data && (
                                <TabsTrigger value="recipe" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-recipe">
                                    <UtensilsCrossed className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Recipe</span>
                                </TabsTrigger>
                            )}
                            {bookmark.place_data && (
                                <TabsTrigger value="place" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-place">
                                    <MapPin className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Place</span>
                                </TabsTrigger>
                            )}
                            <TabsTrigger value="summary" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-summary">
                                <BrainCircuit className="w-5 h-5 sm:mr-1.5" />
                                <span className="hidden sm:inline">Summary</span>
                            </TabsTrigger>
                            <TabsTrigger value="transcript" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-transcript">
                                <ScrollText className="w-5 h-5 sm:mr-1.5" />
                                <span className="hidden sm:inline">{bookmark.content_type === 'video' ? 'Transcript' : 'Source'}</span>
                            </TabsTrigger>
                            <TabsTrigger value="notes" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-notes">
                                <MessageSquareText className="w-5 h-5 sm:mr-1.5" />
                                <span className="hidden sm:inline">Notes</span>
                            </TabsTrigger>
                        </TabsList>

                        {/* Ask BrainSync - inline expanding panel.
                            Collapsed: looks like the dashed "Add a note"
                            affordance (quiet, low-contrast). Expanded:
                            grows in-place pushing the AI summary / tabs
                            / recommendations down the page (no overlay,
                            no drawer). Only mounted once there is
                            something for the AI to talk about. */}
                        {!editing && (bookmark.summary || bookmark.transcript) && bookmark.status === 'completed' && (
                            isAnonymous ? (
                                <AskBookmarkPreview bookmarkId={bookmark.bookmark_id} deviceId={anonParams.deviceId} />
                            ) : (
                                <AskBookmarkInline
                                    bookmarkId={bookmark.bookmark_id}
                                    bookmarkTitle={bookmark.title}
                                    lastQuestion={askLastQuestion}
                                    onMessagesChange={(messages) => {
                                        // Keep "Continue: …" hint in sync
                                        // with the live thread without a
                                        // round-trip when collapsing.
                                        const lastUser = [...(messages || [])].reverse().find(m => m.role === 'user');
                                        setAskLastQuestion(lastUser?.content || '');
                                    }}
                                />
                            )
                        )}


                        {/* Recipe Tab */}
                        {bookmark.recipe_data && (
                            <TabsContent value="recipe">
                                <div className="bg-white border border-border/60 rounded-2xl p-5">
                                    <RecipeView recipe={bookmark.recipe_data} url={bookmark.url} />
                                </div>
                            </TabsContent>
                        )}

                        {/* Song Deep-Dive Tab */}
                        {bookmark.content_subtype === 'song' && (
                            <TabsContent value="song">
                                <SongDeepDive bookmark={bookmark} apiBase={API} />
                            </TabsContent>
                        )}

                        {/* Place Tab */}
                        {bookmark.place_data && (
                            <TabsContent value="place">
                                <div className="bg-white border border-border/60 rounded-2xl p-5">
                                    <PlaceView place={bookmark.place_data} url={bookmark.url} hideCoverPhoto={!!bookmark.thumbnail} />
                                </div>
                            </TabsContent>
                        )}

                        <TabsContent value="summary">
                            <div className="bg-white border border-border/60 rounded-2xl p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                                            <Sparkles className="w-4 h-4 text-accent-foreground" />
                                        </div>
                                        {bookmark.content_subtype === 'website' ? 'Website Analysis' : bookmark.content_subtype === 'hotel' ? 'Hotel Analysis' : bookmark.content_subtype === 'place' ? 'Place Summary' : 'AI Summary'}
                                    </h3>
                                    {bookmark.summary && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => handleCopy(bookmark.summary)}
                                            className="rounded-full"
                                        >
                                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                        </Button>
                                    )}
                                </div>
                                {bookmark.status === 'processing' || bookmark.status === 'pending' ? (
                                    <ProcessingBlockPlaceholder stage={bookmark.processing_stage || 'extracting'} label="Generating summary..." />
                                ) : bookmark.summary && bookmark.content_subtype === 'hotel' ? (
                                    <HotelAnalysisView summary={bookmark.summary} url={bookmark.url} />
                                ) : bookmark.summary && bookmark.content_subtype === 'website' ? (
                                    <WebsiteAnalysisView summary={bookmark.summary} url={bookmark.url} />
                                ) : bookmark.summary ? (
                                    <div className="prose prose-sm max-w-none">
                                        {renderSummary(bookmark.summary)}
                                    </div>
                                ) : bookmark.is_premium === false ? (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-5 text-center" data-testid="basic-bookmark-cta">
                                        <p className="text-sm font-medium text-amber-900 mb-1">Saved as Basic - no AI summary yet</p>
                                        <p className="text-xs text-amber-800/80 mb-3">
                                            You ran out of Premium AI saves when you added this bookmark.
                                            Upgrade or use a Reprocess to generate the summary, transcript, and tags.
                                        </p>
                                        <Button
                                            onClick={handleReprocess}
                                            className="rounded-xl bg-primary text-white hover:bg-primary/90"
                                            data-testid="basic-bookmark-reprocess-btn"
                                        >
                                            <RefreshCw className="w-4 h-4 mr-1.5" /> Try Reprocess
                                        </Button>
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-12">
                                        No summary available. Try reprocessing.
                                    </p>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="transcript">
                            <div className="bg-white border border-border/60 rounded-2xl p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                                            <FileText className="w-4 h-4" />
                                        </div>
                                        {bookmark.content_type === 'video' ? 'Transcript' : 'Source Text'}
                                    </h3>
                                    <div className="flex items-center gap-1">
                                        {!editingTranscript && (
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={startEditingTranscript}
                                                className="rounded-full text-xs"
                                                data-testid="edit-transcript-btn"
                                            >
                                                <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                                            </Button>
                                        )}
                                        {bookmark.transcript && !editingTranscript && (
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={() => handleCopy(bookmark.transcript)}
                                                className="rounded-full"
                                            >
                                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                {editingTranscript ? (
                                    <div className="space-y-3">
                                        <Textarea
                                            value={inlineTranscript}
                                            onChange={(e) => setInlineTranscript(e.target.value)}
                                            className="min-h-[300px] rounded-xl resize-none text-sm"
                                            placeholder="Paste the full source text here..."
                                            dir={isHebrew(inlineTranscript) ? 'rtl' : 'ltr'}
                                            data-testid="transcript-textarea"
                                        />
                                        <div className="flex gap-2 justify-end">
                                            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setEditingTranscript(false)} disabled={savingTranscript}>Cancel</Button>
                                            <Button size="sm" className="rounded-xl" onClick={saveTranscript} disabled={savingTranscript} data-testid="save-transcript-btn">
                                                {savingTranscript ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Saving...</> : 'Save'}
                                            </Button>
                                        </div>
                                    </div>
                                ) : bookmark.status === 'processing' || bookmark.status === 'pending' ? (
                                    <ProcessingBlockPlaceholder stage={bookmark.processing_stage || 'extracting'} label="Processing content..." />
                                ) : bookmark.transcript ? (
                                    <ScrollArea className="h-[400px]">
                                        <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap" dir={isHebrew(bookmark.transcript) ? 'rtl' : 'ltr'}>
                                            {bookmark.transcript}
                                        </p>
                                    </ScrollArea>
                                ) : bookmark.url ? (
                                    <div className="text-center py-10">
                                        <p className="text-muted-foreground mb-4">
                                            {bookmark.content_type === 'video'
                                                ? 'No transcript available for this video.'
                                                : 'View the full content on the original page.'}
                                        </p>
                                        <a
                                            href={bookmark.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                                            data-testid="view-original-link"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            {bookmark.content_type === 'video' ? 'Watch original video' : 'View original article'}
                                        </a>
                                    </div>
                                ) : (
                                    <div className="text-center py-10">
                                        <p className="text-muted-foreground mb-3">
                                            {bookmark.content_type === 'video' 
                                                ? 'No transcript available. Try reprocessing.'
                                                : 'Source text could not be extracted from this link.'}
                                        </p>
                                        <Button variant="outline" size="sm" className="rounded-xl" onClick={startEditingTranscript} data-testid="add-source-text-btn">
                                            <Edit2 className="w-3.5 h-3.5 mr-1" /> Add source text manually
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="notes">
                            {/* Notes tab uses the SAME compact card as the
                                inline "Your Note" / "Add a note..." prompt
                                that lives above the tab strip. We
                                deliberately do NOT render a heavier
                                "Personal Notes" header here - the user
                                expects identical UX whether they tap the
                                bubble icon or the "Add a note..." strip. */}
                            {(bookmark.notes || (noteImages && noteImages.length > 0) || editingNotes) ? (
                                <div className="bg-white border border-border/60 rounded-2xl p-4" data-testid="notes-tab-card">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <StickyNote className="w-3.5 h-3.5 text-primary" />
                                            <span className="text-xs font-semibold text-muted-foreground">Your Note</span>
                                        </div>
                                        {!editingNotes ? (
                                            <Button variant="ghost" size="sm" className="rounded-full h-7 px-2 text-xs" onClick={startEditingNotes} data-testid="edit-note-tab-btn">
                                                <Edit2 className="w-3 h-3 mr-1" /> Edit
                                            </Button>
                                        ) : (
                                            <div className="flex items-center gap-1">
                                                <Button variant="ghost" size="sm" className="rounded-full h-7 px-2 text-xs" onClick={() => setEditingNotes(false)}>
                                                    Cancel
                                                </Button>
                                                <Button size="sm" className="rounded-full h-7 px-3 text-xs" onClick={saveNotes} data-testid="save-note-tab-btn">
                                                    <Save className="w-3 h-3 mr-1" /> Save
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    {editingNotes ? (
                                        <div className="space-y-2">
                                            <Textarea
                                                value={inlineNotes}
                                                onChange={(e) => setInlineNotes(e.target.value)}
                                                placeholder="Add your personal notes here..."
                                                className="min-h-[100px] rounded-xl text-sm"
                                                autoFocus
                                                data-testid="notes-tab-textarea"
                                            />
                                            <button
                                                onClick={() => noteImageInputRef.current?.click()}
                                                disabled={uploadingImage}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
                                                data-testid="add-photo-to-note-tab-btn"
                                            >
                                                {uploadingImage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                                                Add photo
                                            </button>
                                        </div>
                                    ) : (
                                        <NoteContent text={bookmark.notes} dir={isHebrew(bookmark.notes) ? 'rtl' : 'ltr'} />
                                    )}
                                    {/* Note images */}
                                    {noteImages && noteImages.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {noteImages.map((imgUrl, i) => (
                                                <div key={imgUrl} className="relative group/img">
                                                    <img src={imgUrl} alt="" className="w-20 h-20 rounded-lg object-cover border border-border/40" data-testid={`note-tab-image-${i}`} />
                                                    {editingNotes && (
                                                        <button
                                                            onClick={() => deleteNoteImage(i)}
                                                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-sm"
                                                            data-testid={`delete-note-tab-image-${i}`}
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* No note yet → identical dashed "Add a note..." affordance */
                                <button
                                    onClick={startEditingNotes}
                                    className="w-full flex items-center gap-2 p-3 rounded-xl border border-dashed border-border hover:border-primary/30 hover:bg-secondary/30 transition-colors text-sm text-muted-foreground"
                                    data-testid="add-note-tab-btn"
                                >
                                    <MessageSquarePlus className="w-3.5 h-3.5" />
                                    Add a note...
                                </button>
                            )}
                        </TabsContent>
                        {/* Hidden file inputs - always in DOM */}
                        <input
                            ref={noteImageInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => { if (e.target.files?.[0]) uploadImageToNotes(e.target.files[0]); e.target.value = ''; }}
                        />
                    </Tabs>
                </motion.div>
            </main>

            {/* Recommendations Section */}
            {bookmark?.status === 'completed' && (recommendations?.related_bookmarks?.length > 0 || recommendations?.suggested_content?.length > 0) && (
                <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-8" data-testid="recommendations-section">
                    {/* Related Bookmarks */}
                    {recommendations.related_bookmarks?.length > 0 && (
                        <div className="mb-7">
                            <div className="flex items-center gap-2.5 mb-3" data-testid="related-bookmarks-header">
                                <span className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                                    <Bookmark className="w-3.5 h-3.5" />
                                </span>
                                <h3 className="text-[15px] sm:text-base font-bold text-zinc-900 tracking-tight">
                                    Related Bookmarks
                                </h3>
                                <span aria-hidden="true" className="flex-1 h-px bg-gradient-to-r from-emerald-200/70 via-emerald-100/40 to-transparent" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {recommendations.related_bookmarks.map(rb => (
                                    <button
                                        key={rb.bookmark_id}
                                        onClick={() => { setRecommendations(null); navigate(`/bookmark/${rb.bookmark_id}`); }}
                                        className="flex items-start gap-3 p-3 rounded-xl bg-white border border-border/60 hover:border-primary/20 hover:shadow-sm transition-all text-left group"
                                        data-testid={`related-bookmark-${rb.bookmark_id}`}
                                    >
                                        {rb.thumbnail ? (
                                            <img src={rb.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                                        ) : (
                                            <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                                                {rb.content_type === 'video' ? <Play className="w-5 h-5 text-muted-foreground" /> : <FileText className="w-5 h-5 text-muted-foreground" />}
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{rb.title}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{rb.summary}</p>
                                            <div className="flex items-center gap-1 mt-1.5">
                                                {rb.tags?.slice(0, 3).map(t => (
                                                    <span key={t} className="px-1.5 py-0.5 rounded bg-secondary text-[10px] font-mono text-muted-foreground">{t}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* AI Suggested Content */}
                    {recommendations.suggested_content?.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2.5 mb-3" data-testid="explore-more-header">
                                <span className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-amber-100 text-amber-700 ring-1 ring-amber-200">
                                    <Compass className="w-3.5 h-3.5" />
                                </span>
                                <h3 className="text-[15px] sm:text-base font-bold text-zinc-900 tracking-tight">
                                    Explore More
                                </h3>
                                <span aria-hidden="true" className="flex-1 h-px bg-gradient-to-r from-amber-200/70 via-amber-100/40 to-transparent" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                {recommendations.suggested_content.map((sc, i) => (
                                    <a
                                        key={`rec-${sc.title?.slice(0, 20) || i}`}
                                        href={sc.url || `https://www.google.com/search?q=${encodeURIComponent(sc.title)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex flex-col p-3 rounded-xl bg-white border border-border/60 hover:border-primary/20 hover:shadow-sm transition-all group"
                                        data-testid={`suggested-content-${i}`}
                                    >
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
                                                sc.type === 'video' ? 'bg-red-50 text-red-500' : 
                                                sc.type === 'tutorial' ? 'bg-blue-50 text-blue-500' : 
                                                sc.type === 'tool' ? 'bg-purple-50 text-purple-500' : 
                                                'bg-emerald-50 text-emerald-500'
                                            }`}>
                                                {sc.type === 'video' ? <Play className="w-3 h-3" /> : 
                                                 sc.type === 'tutorial' ? <GraduationCap className="w-3 h-3" /> :
                                                 sc.type === 'tool' ? <Zap className="w-3 h-3" /> :
                                                 <FileText className="w-3 h-3" />}
                                            </div>
                                            <span className="text-[10px] font-mono uppercase text-muted-foreground">{sc.source || sc.type}</span>
                                            <ExternalLinkIcon className="w-3 h-3 text-muted-foreground/0 group-hover:text-muted-foreground ml-auto transition-colors" />
                                        </div>
                                        <p className="text-sm font-medium leading-snug group-hover:text-primary transition-colors">{sc.title}</p>
                                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{sc.description}</p>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            )}

            {/* Loading state for recommendations */}
            {bookmark?.status === 'completed' && recsLoading && (
                <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-8 flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Finding related content...
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete bookmark?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete this bookmark and its summary. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <ShareModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                kind="bookmark"
                item={bookmark}
                currentUser={user}
                onVisibilityChange={(v) => setBookmark((prev) => prev ? { ...prev, share_visibility: v } : prev)}
            />

            {/* Ask BrainSync moved inline above - see AskBookmarkInline
                rendered alongside the Tabs. No bottom-sheet here. */}

            {isAnonymous && (
                <AnonKeepForeverBar bookmark={bookmark} deviceId={anonParams.deviceId} />
            )}
        </div>
    );
};

// ── Ask BrainSync preview (anonymous try-it mode) ──
// Anonymous visitors get ONE free question to feel the AI magic
// firsthand. Backend enforces the limit (`anon_ask_used` on the anon
// bookmark doc); on second attempt the server returns 402 and we
// open the sign-up sheet. Anything Claude-powered beyond the first
// question requires an account.
function AskBookmarkPreview({ bookmarkId, deviceId }) {
    const [sheetOpen, setSheetOpen] = React.useState(false);
    const [showHint, setShowHint] = React.useState(true);
    // Anonymous mode: start expanded so the input is visible the
    // moment the bookmark page renders. Registered users' first-time
    // AskBookmarkInline also opens by default with the same coach
    // tooltip pattern — matching that here keeps the two flows
    // visually consistent. (Product decision Aug 26 2026.)
    const [expanded, setExpanded] = React.useState(true);
    const [question, setQuestion] = React.useState('');
    const [answer, setAnswer] = React.useState('');
    const [askedQuestion, setAskedQuestion] = React.useState('');
    const [asking, setAsking] = React.useState(false);
    const [error, setError] = React.useState('');
    const usedRef = React.useRef(false);

    // Auto-dismiss the coach tooltip after 8 s (a touch longer than the
    // collapsed version because there's more to read).
    React.useEffect(() => {
        if (!showHint) return;
        const t = setTimeout(() => setShowHint(false), 8000);
        return () => clearTimeout(t);
    }, [showHint]);

    const openSignup = () => {
        try {
            const payload = { bookmark_id: bookmarkId, device_id: deviceId, ts: Date.now() };
            localStorage.setItem('brainsync_pending_claim', JSON.stringify(payload));
        } catch (_) { /* private mode */ }
        setSheetOpen(true);
    };

    const submitQuestion = async () => {
        const q = question.trim();
        if (!q) return;
        // Second attempt (client-side short-circuit): open signup.
        if (usedRef.current) { openSignup(); return; }
        setAsking(true);
        setError('');
        try {
            const res = await fetch(`/api/try/bookmark/${bookmarkId}/ask`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ device_id: deviceId, question: q }),
            });
            if (res.status === 402) {
                // Server says the free question was already used
                // (e.g. user refreshed the page between calls). Open
                // signup so they can keep asking.
                usedRef.current = true;
                openSignup();
                return;
            }
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                setError(err.detail || 'Couldn\u2019t ask right now — try again.');
                return;
            }
            const data = await res.json();
            setAnswer(data.answer || '');
            setAskedQuestion(q);
            setQuestion('');
            usedRef.current = true;
        } catch (_) {
            setError('Network problem. Please try again.');
        } finally {
            setAsking(false);
        }
    };

    if (!expanded) {
        return (
            <div className="my-6 relative" data-testid="ask-preview">
                <button
                    type="button"
                    onClick={() => { setExpanded(true); setShowHint(false); }}
                    className="w-full rounded-2xl border border-dashed border-emerald-500/40 hover:border-emerald-500/70 bg-emerald-50/40 hover:bg-emerald-50/70 transition-colors px-5 py-4 flex items-center gap-3 text-left"
                    data-testid="ask-preview-btn"
                >
                    <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: '#10B981' }}>
                        <MessageCircleQuestion className="w-4 h-4 text-white" />
                    </span>
                    <span className="flex-1 min-w-0">
                        <span className="block text-[14.5px] font-semibold" style={{ color: '#111827' }}>Ask BrainSync anything about this</span>
                        <span className="block text-[12.5px]" style={{ color: '#5F6D66' }}>What are the key takeaways? Give me a summary in 3 lines…</span>
                    </span>
                    <span className="hidden sm:inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-mono uppercase tracking-widest" style={{ background: '#E3F1E9', color: '#1F7A55' }}>
                        <Sparkles className="w-3 h-3" /> 1 free ask
                    </span>
                </button>
                {showHint && (
                    <div
                        className="absolute -top-2 right-4 -translate-y-full px-3 py-2 rounded-xl shadow-lg text-[12px] font-medium max-w-[220px] animate-in fade-in slide-in-from-bottom-1"
                        style={{ background: '#0F172A', color: '#fff' }}
                        role="tooltip"
                    >
                        Try one free question about this bookmark.
                        <span className="absolute right-6 -bottom-1 w-2 h-2 rotate-45" style={{ background: '#0F172A' }} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="my-6 relative" data-testid="ask-preview-expanded">
            {showHint && !askedQuestion && (
                <div
                    className="absolute left-4 right-4 sm:right-auto sm:max-w-[360px] -top-3 -translate-y-full z-20 rounded-2xl shadow-xl border p-3 pl-3 flex items-start gap-2.5 animate-in fade-in slide-in-from-bottom-1"
                    style={{ background: '#FFFFFF', borderColor: '#E4E8E3' }}
                    role="tooltip"
                    data-testid="ask-preview-hint"
                >
                    <span className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: 'rgba(16,185,129,.15)' }}>
                        <Sparkles className="w-4 h-4" style={{ color: '#10B981' }} />
                    </span>
                    <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-bold" style={{ color: '#111827' }}>Try Ask BrainSync</p>
                        <p className="text-[12.5px] leading-snug mt-0.5" style={{ color: '#5F6D66' }}>Get answers about this bookmark — key takeaways, quick summaries, and more.</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowHint(false)}
                        className="w-6 h-6 grid place-items-center shrink-0 rounded-full hover:bg-black/5"
                        aria-label="Dismiss"
                        data-testid="ask-preview-hint-close"
                    >
                        <XIcon className="w-3.5 h-3.5" style={{ color: '#8A968F' }} />
                    </button>
                    <span className="absolute left-8 -bottom-1 w-2.5 h-2.5 rotate-45 border-r border-b" style={{ background: '#FFFFFF', borderColor: '#E4E8E3' }} />
                </div>
            )}
            <div className="rounded-2xl border border-emerald-500/40 bg-emerald-50/20 overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-3 border-b border-emerald-500/20">
                    <span className="w-8 h-8 rounded-xl grid place-items-center" style={{ background: '#10B981' }}>
                        <MessageCircleQuestion className="w-4 h-4 text-white" />
                    </span>
                    <span className="text-[14px] font-semibold" style={{ color: '#111827' }}>Ask BrainSync</span>
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest" style={{ background: '#E3F1E9', color: '#1F7A55' }}>
                        <Sparkles className="w-3 h-3" /> {usedRef.current ? 'Used' : '1 free ask'}
                    </span>
                </div>
            {askedQuestion && (
                <div className="px-5 pt-4">
                    <p className="text-[13px] font-medium mb-2" style={{ color: '#5F6D66' }}>You asked</p>
                    <p className="text-[14.5px] mb-4" style={{ color: '#111827' }}>{askedQuestion}</p>
                    <p className="text-[13px] font-medium mb-2" style={{ color: '#5F6D66' }}>BrainSync</p>
                    <p className="text-[14.5px] leading-relaxed whitespace-pre-wrap" style={{ color: '#111827' }} data-testid="ask-preview-answer">{answer}</p>
                </div>
            )}
            <div className="p-3">
                <div className="flex items-end gap-2 rounded-xl bg-white border border-emerald-500/30 p-2 focus-within:border-emerald-500/70">
                    <textarea
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submitQuestion();
                            }
                        }}
                        rows={1}
                        placeholder={usedRef.current ? 'Sign up to keep asking…' : 'Ask a question…'}
                        className="flex-1 resize-none bg-transparent outline-none text-[14.5px] py-1.5 px-1 min-h-[36px] max-h-32"
                        style={{ color: '#111827' }}
                        data-testid="ask-preview-input"
                    />
                    <button
                        type="button"
                        onClick={usedRef.current ? openSignup : submitQuestion}
                        disabled={asking || (!question.trim() && !usedRef.current)}
                        className="rounded-lg font-bold text-[13px] px-3.5 h-9 shrink-0 disabled:opacity-40"
                        style={{ background: '#10B981', color: '#fff' }}
                        data-testid="ask-preview-submit"
                    >
                        {asking ? '…' : (usedRef.current ? 'Sign up' : 'Ask')}
                    </button>
                </div>
                {error && <p className="text-[12px] text-red-600 mt-2 px-1">{error}</p>}
                {usedRef.current && !error && (
                    <p className="text-[11.5px] mt-2 px-1" style={{ color: '#5F6D66' }}>You&apos;ve used your free question. Sign up to keep asking.</p>
                )}
            </div>
            </div>
            <EmailAuthSheet
                open={sheetOpen}
                initialMode="register"
                onOpenChange={setSheetOpen}
            />
        </div>
    );
}

// ── Anonymous try-it "Keep this forever" sticky CTA ──
// Overlays the bottom of BookmarkDetail when the page was reached via
// `?anon=1&device_id=X` (from the TryIt flow). Renders the auth sheet
// inline (rather than navigating to `/?signup=1`) so:
//   1. the sheet appears on top of the actual bookmark the user is
//      committing to — not the landing page they left minutes ago,
//   2. there's no page transition to stutter through when OAuth
//      returns from the Custom Tab / Google Sign-In sheet,
//   3. after `setUser` fires, BookmarkDetail's own `user` guard swaps
//      the `?anon=1` view for the authenticated `/bookmark/{id}` view
//      — one clean transition instead of three.
// The pending-claim is stashed in localStorage before opening the
// sheet so `AuthContext.checkAuth` migrates the bookmark server-side
// as soon as auth resolves.
function AnonKeepForeverBar({ bookmark, deviceId }) {
    const [sheetOpen, setSheetOpen] = React.useState(false);
    const onKeep = () => {
        try {
            const payload = { bookmark_id: bookmark.bookmark_id, device_id: deviceId, ts: Date.now() };
            localStorage.setItem('brainsync_pending_claim', JSON.stringify(payload));
        } catch (_) { /* private mode */ }
        setSheetOpen(true);
    };
    return (
        <>
            {/* Spacer so the sticky bar never covers content when scrolled to bottom */}
            <div className="h-[92px]" aria-hidden="true" />
            <div
                className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/60 backdrop-blur-md"
                style={{
                    background: 'rgba(253,252,248,.92)',
                    paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
                    paddingTop: '14px',
                }}
                data-testid="anon-keep-forever-bar"
            >
                <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
                    <div className="min-w-0 hidden sm:block">
                        <p className="text-[13px] font-semibold text-foreground truncate">Loved it? Keep it forever.</p>
                        <p className="text-[12px] text-muted-foreground">Sign up in one tap — this bookmark comes with you.</p>
                    </div>
                    <button
                        onClick={onKeep}
                        className="rounded-full font-bold text-[15px] min-h-[52px] px-6 flex-1 sm:flex-none inline-flex items-center justify-center gap-2"
                        style={{ background: '#10B981', color: '#fff' }}
                        data-testid="anon-keep-forever-btn"
                    >
                        Keep this forever
                    </button>
                </div>
            </div>
            <EmailAuthSheet
                open={sheetOpen}
                initialMode="register"
                onOpenChange={setSheetOpen}
            />
        </>
    );
}

export default BookmarkDetail;
